import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PageHeader from '../../components/PageHeader';
import { PaginationControls } from '../../components/PaginationControls';
import MarkSheetModal from '../../components/MarkSheetModal';

const IFProgramEnrollStudent = () => {
  const navigate = useNavigate();
  const [studentEnrollmentData, setStudentEnrollmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Filter options from API
  const [programOptions, setProgramOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [filteredStudentOptions, setFilteredStudentOptions] = useState([]);

  // Current filter values for react-select
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Current filter values for API
  const [currentFilters, setCurrentFilters] = useState({
    program_id: null,
    batch_id: null,
    student_id: null
  });

  // API configuration
  const institute_id = sessionStorage.getItem('studentInstituteId');
  const teacher_id = sessionStorage.getItem('id');
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  // Storage keys for persistent filters
  const STORAGE_KEYS = {
    FILTERS: 'if_enrollment_filters',
    SELECTED_OPTIONS: 'if_enrollment_selected_options',
    CURRENT_PAGE: 'if_enrollment_current_page'
  };

  // Custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#86b7fe' : '#dee2e6'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  };

  // Save filters to sessionStorage
  const saveFiltersToStorage = (filters, selectedOptions, page) => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(filters));
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_OPTIONS, JSON.stringify(selectedOptions));
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_PAGE, page.toString());
    } catch (error) {
      console.warn('Failed to save filters to storage:', error);
    }
  };

  // Load filters from sessionStorage
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem(STORAGE_KEYS.FILTERS);
      const savedSelectedOptions = sessionStorage.getItem(STORAGE_KEYS.SELECTED_OPTIONS);
      const savedPage = sessionStorage.getItem(STORAGE_KEYS.CURRENT_PAGE);

      return {
        filters: savedFilters ? JSON.parse(savedFilters) : { program_id: null, batch_id: null, student_id: null },
        selectedOptions: savedSelectedOptions ? JSON.parse(savedSelectedOptions) : { program: null, batch: null, student: null },
        page: savedPage ? parseInt(savedPage, 10) : 1
      };
    } catch (error) {
      console.warn('Failed to load filters from storage:', error);
      return {
        filters: { program_id: null, batch_id: null, student_id: null },
        selectedOptions: { program: null, batch: null, student: null },
        page: 1
      };
    }
  };

  // Clear filters from storage
  const clearFiltersFromStorage = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.FILTERS);
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_OPTIONS);
      sessionStorage.removeItem(STORAGE_KEYS.CURRENT_PAGE);
    } catch (error) {
      console.warn('Failed to clear filters from storage:', error);
    }
  };

  // Fetch Programs
  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${LMS}/api/institutes/${institute_id}/programs`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (data.status && data.data) {
        const programs = data.data.map(program => ({
          label: program.program_title || program.title,
          value: program.id,
          id: program.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        setProgramOptions(programs);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  // Fetch Batches
  const fetchBatches = async () => {
    try {
      const response = await fetch(`${LMS}/api/batch/getlist`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teacher_id: teacher_id
        })
      });

      const data = await response.json();

      if (data.status && data.data && Array.isArray(data.data.data)) {
        const batches = data.data.data.map(batch => ({
          label: batch.batch_name || batch.name,
          value: batch.id,
          id: batch.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        setBatchOptions(batches);
      } else if (data.status && Array.isArray(data.data)) {
        // Fallback if data.data is the array itself
        const batches = data.data.map(batch => ({
          label: batch.batch_name || batch.name,
          value: batch.id,
          id: batch.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        setBatchOptions(batches);
      }
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  };


  // Fetch all students
  const fetchAllStudents = async (pid) => {
    try {
      const programIds = pid != null ? pid : "all";
      const res = await fetch(`${LMS}/api/students/by-program`, {
        method: "POST",
        headers: {
          Authorization: token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          institution_id: institute_id,
          program_ids: programIds,
        }),
      });

      const data = await res.json();

      if (data.status) {
        const formattedStudents = data.data.map((s) => ({
          label: `${s.first_name} ${s.last_name}`,
          value: s.id,
          program_id: s.program_id,
        }));

        setAllStudents(formattedStudents);
        setFilteredStudentOptions(formattedStudents);
      }
    } catch (err) {
      console.error("Student fetch error:", err);
    }
  };

  // Fetch Enrollment Data
  const fetchEnrollmentData = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('user_id', teacher_id);
      params.append('institute_id', institute_id);
      params.append('page', page);

      // Add filter parameters if they exist
      if (filters.program_id) {
        params.append('program_id', filters.program_id);
      }
      if (filters.batch_id) {
        params.append('batch_id', filters.batch_id);
      }
      if (filters.student_id) {
        params.append('student_id', filters.student_id);
      }

      const response = await fetch(`${LMS}/api/batch-enrollments?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match component's expected format
      const transformedData = transformApiData(data?.data);
      setStudentEnrollmentData(transformedData);

      // Set pagination information
      if (data.status && data.data && data.data.pagination) {
        const paginationData = data.data.pagination;
        setCurrentPage(paginationData.current_page);
        setTotalPages(paginationData.last_page);
        setTotalRecords(paginationData.total);
        setPerPage(paginationData.per_page);
      }

      // Set program options from API response
      if (data.status && data.data && data.data.programs && data.data.programs.length > 0) {
        const programs = data.data.programs.map(program => ({
          label: program.program_title,
          value: program.id,
          id: program.id
        })).sort((a, b) => a.label.localeCompare(b.label));

        setProgramOptions(programs);
      }

    } catch (err) {
      console.error('Error fetching enrollment data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Transform API response to match component's data structure
  const transformApiData = (apiResponse) => {
    if (apiResponse.enrollments && Array.isArray(apiResponse.enrollments)) {
      return apiResponse.enrollments.map((item) => ({
        id: item.id,
        serialNo: item.serial_no,
        student: item.student_name,
        program: item.program_name,
        batch: item.batch_name,
        division: item.division || 'N/A',
        session: item.session,
        type: item.enrollment_type,
        date: item.enrollment_date,
        enrollmentNumber: item.id,
        institute: item.institution_name,
        creditsUsed: item.credits_used,
        studentId: item.student_id,
        programId: item.program_id,
        batchId: item.batch_id,
        institutionId: item.institution_id,
        paymentId: item.payment_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        moduleProgress: item.module_progress || { completed_modules: 0, total_modules: 0 },
      }));
    }

    return [];
  };

  useEffect(() => {
    // Load saved filters on component mount
    const savedData = loadFiltersFromStorage();

    // Set initial state from saved data
    setCurrentFilters(savedData.filters);
    setCurrentPage(savedData.page);

    // Fetch initial data
    fetchPrograms();
    fetchBatches();
    fetchAllStudents().then(() => {
      restoreSelectedOptions(savedData.selectedOptions);
    });

    fetchEnrollmentData(savedData.page, savedData.filters);
  }, []);

  // Restore selected options after options are loaded
  const restoreSelectedOptions = (savedSelectedOptions) => {
    if (savedSelectedOptions.program && programOptions.length > 0) {
      const program = programOptions.find(p => p.value === savedSelectedOptions.program.value);
      if (program) {
        setSelectedProgram(program);
      }
    }

    if (savedSelectedOptions.batch && batchOptions.length > 0) {
      const batch = batchOptions.find(b => b.value === savedSelectedOptions.batch.value);
      if (batch) {
        setSelectedBatch(batch);
      }
    }

    if (savedSelectedOptions.student && filteredStudentOptions.length > 0) {
      const student = filteredStudentOptions.find(s => s.value === savedSelectedOptions.student.value);
      if (student) {
        setSelectedStudent(student);
      }
    }
  };

  // Update restoreSelectedOptions calls when options change
  useEffect(() => {
    if (programOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.program) {
        const program = programOptions.find(p => p.value === savedData.selectedOptions.program.value);
        if (program) {
          setSelectedProgram(program);
        }
      }
    }
  }, [programOptions]);

  useEffect(() => {
    if (batchOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.batch) {
        const batch = batchOptions.find(b => b.value === savedData.selectedOptions.batch.value);
        if (batch) {
          setSelectedBatch(batch);
        }
      }
    }
  }, [batchOptions]);

  useEffect(() => {
    if (filteredStudentOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.student) {
        const student = filteredStudentOptions.find(s => s.value === savedData.selectedOptions.student.value);
        if (student) {
          setSelectedStudent(student);
        }
      }
    }
  }, [filteredStudentOptions]);

  const handleView = (id) => {
    // Save current filters before navigating
    const selectedOptions = {
      program: selectedProgram,
      batch: selectedBatch,
      student: selectedStudent
    };
    saveFiltersToStorage(currentFilters, selectedOptions, currentPage);

    navigate(`/student-enrollment/${id}`);
  };

  const handleRefresh = () => {
    // Clear stored filters on refresh
    clearFiltersFromStorage();
    setFilteredStudentOptions([]);
    setSelectedProgram(null);
    setSelectedBatch(null);
    setSelectedStudent(null);
    setCurrentFilters({ program_id: null, batch_id: null, student_id: null });
    setCurrentPage(1);
    fetchAllStudents();
    fetchEnrollmentData(1, { program_id: null, batch_id: null, student_id: null });
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);

      // Save current state to storage
      const selectedOptions = {
        program: selectedProgram,
        batch: selectedBatch,
        student: selectedStudent
      };
      saveFiltersToStorage(currentFilters, selectedOptions, page);
      fetchEnrollmentData(page, currentFilters);
    }
  };

  // Handle program filter change
  const handleProgramChange = (selectedOption) => {
    setSelectedProgram(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.program_id = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedOption,
      batch: selectedBatch,
      student: selectedStudent
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchAllStudents(selectedOption ? selectedOption.value : null);
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle batch filter change
  const handleBatchChange = (selectedOption) => {
    setSelectedBatch(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.batch_id = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedProgram,
      batch: selectedOption,
      student: selectedStudent
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle student filter change
  const handleStudentChange = (selectedOption) => {
    setSelectedStudent(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.student_id = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedProgram,
      batch: selectedBatch,
      student: selectedOption
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const resetFilters = {
      program_id: null,
      batch_id: null,
      student_id: null
    };
    setCurrentFilters(resetFilters);
    const newPage = 1;
    setCurrentPage(newPage);
    setSelectedProgram(null);
    setSelectedBatch(null);
    setSelectedStudent(null);

    // Clear stored filters
    clearFiltersFromStorage();

    // Reset student options to show all students
    setFilteredStudentOptions(allStudents);

    fetchEnrollmentData(newPage, resetFilters);
  };

  const [showMarkSheetModal, setShowMarkSheetModal] = useState(false);
  const [selectedStudentForMarkSheet, setSelectedStudentForMarkSheet] = useState(null);

  const handleViewMarkSheet = (id) => {
    // Find the student details from the existing data
    const student = studentEnrollmentData.find(s => s.studentId === id);
    // Construct a student object with the name (since our modal expects 'name')
    // The existing data has 'student' field which is the name
    setSelectedStudentForMarkSheet({
      id: id,
      name: student ? student.student : 'Unknown'
    });
    setShowMarkSheetModal(true);
  };

  // Define columns
  const columns = [
    { key: 'serialNo', label: 'Sr No.' },
    { key: 'student', label: 'Student' },
    { key: 'program', label: 'Program' },
    { key: 'batch', label: 'Batch' },
    {
      key: 'moduleProgress',
      label: 'Module Progress',
      render: (value) => {
        if (!value || !value.total_modules) {
          return '-';
        }

        const { completed_modules = 0, total_modules = 0 } = value;
        return `${completed_modules}/${total_modules}`;
      }
    },
    {
      key: 'date',
      label: 'Date',
      render: value => {
        try {
          return new Date(value).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (e) {
          return value;
        }
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, item) => {
        const { completed_modules = 0, total_modules = 0 } = item.moduleProgress || {};
        const isComplete = total_modules > 0 && completed_modules === total_modules;

        return (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm text-white"
              style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
              onClick={() => handleView(item?.studentId)}
            >
              View Progress
            </button>
            {isComplete && (
              <button
                className="btn btn-sm btn-info text-white"
                onClick={() => handleViewMarkSheet(item?.studentId)}
              >
                View MarkSheet
              </button>
            )}
          </div>
        );
      }
    }
  ];

  if (error) {
    return (
      <>
        <div className="row">
          <div className="col-12">
            <PageHeader title="Enrolled Students"
              breadcrumbs={[
                { label: "Dashboard", path: "/dashboard" },
                { label: "Enrolled Students", active: true }
              ]}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Data</h4>
              <p>Failed to fetch enrollment data: {error}</p>
              <hr />
              <button className="btn btn-outline-danger" onClick={handleRefresh}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PageHeader title="Enrolled Students"
            breadcrumbs={[
              { label: "Dashboard", path: "/dashboard" },
              { label: "Enrolled Students", active: true }
            ]}
          />
        </div>
      </div>

      {/* Custom Filter Section using React Select */}
      <div className="row mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
          <label className="form-label fw-semibold">Filter by Program:</label>
          <Select
            value={selectedProgram}
            onChange={handleProgramChange}
            options={programOptions}
            placeholder="Select a program..."
            isClearable
            isSearchable
            styles={selectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
          <label className="form-label fw-semibold">Filter by Batch:</label>
          <Select
            value={selectedBatch}
            onChange={handleBatchChange}
            options={batchOptions}
            placeholder="Select a batch..."
            isClearable
            isSearchable
            styles={selectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-lg-4 col-md-6 mb-3 mb-lg-0">
          <label className="form-label fw-semibold">Filter by Student:</label>
          <Select
            value={selectedStudent}
            onChange={handleStudentChange}
            options={filteredStudentOptions}
            placeholder={`Select a student... (${filteredStudentOptions.length} available)`}
            isClearable
            isSearchable
            styles={selectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="col-md-2 d-flex align-items-end justify-content-end">
          <button
            className="btn btn-primary me-2"
            onClick={handleFilterReset}
            disabled={!selectedProgram && !selectedBatch && !selectedStudent}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {loading ?
        <div className="py-4">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div> : <>
          {/* Entries info */}
          <div className="mb-3">
            <small className="text-muted">
              Showing {totalRecords > 0 ? ((currentPage - 1) * perPage) + 1 : 0} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
            </small>
          </div>

          {/* Direct Table Implementation */}
          <div className="row">
            <div className="skyblueborder rounded-4">
              <div className="">
                <div className="table-responsive responsive-table">
                  <table className="table table-hover mb-0 fade-in">
                    <thead>
                      <tr>
                        {columns.map(column => (
                          <th key={column.key} className="border-0">
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {studentEnrollmentData && studentEnrollmentData.length > 0 ? (
                        studentEnrollmentData.map((item, index) => (
                          <tr key={index}>
                            {columns.map(column => (
                              <td key={`${index}-${column.key}`} data-label={column.label}>
                                {column.render ? column.render(item[column.key], item) : item[column.key]}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="border-0 p-0 text-center">
                            <div className="empty-state mt-4 mb-3">
                              <div className="icon">
                                <i className="fas fa-clipboard-list text-muted"></i>
                              </div>
                              <h5 className="text-muted mb-2">No Enrollment Records</h5>
                              <p className="text-muted mb-0 small">
                                There are no student enrollment records to display at this time.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-3 mb-3">
                  <PaginationControls
                    currentPage={currentPage}
                    lastPage={totalPages}
                    onPageChange={(page) => handlePageChange(page)}
                    isEnroll={true}
                  />
                </div>
              )}
            </div>
          </div>
        </>}

      <style>
        {`
        .table > thead > tr > th {
            color: black;
            border: none;
            padding: 1rem;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.875rem;
            letter-spacing: 0.5px;
          }

          .table > tbody > tr > td {
            padding: 1rem;
            vertical-align: middle;
            border-bottom: 1px solid #e9ecef;
          }

          .table > tbody > tr:last-child > td {
            border-bottom: none;
          }

          @media (max-width: 768px) {
            .table-responsive {
              border: none;
              box-shadow: none;
            }

            .table {
              margin-bottom: 0;
            }

            .table thead {
              display: none;
            }

            .table tbody {
              display: block;
            }

            .table tbody tr {
              display: block;
              margin-bottom: 1rem;
              border: 1px solid #eeeff0;
              margin-top: 10px;
              border-radius: 12px;
              background: white;
              padding: 0;
              overflow: hidden;
              transition: transform 0.2s ease, box-shadow 0.2s ease;
            }

            .table tbody td {
              display: grid;
              gap:4px;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem 1rem;
              border: none !important;
              border-bottom: 1px solid #f1f5f9 !important;
              font-size: 0.9rem;
            }

            .table tbody td:first-child {
              color: black;
              font-weight: 600;
              border-radius: 0 !important;
            }

            .table tbody td:first-child::before {
              color: #64748b;
              font-weight: 600;
              font-size: 0.8rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-right: 0.5rem;
              flex-shrink: 0;
              min-width: 80px;
            }

            .table tbody td:last-child {
              justify-content: center;
              padding: 1rem;
            }

            .table tbody td::before {
              content: attr(data-label);
              font-weight: 600;
              color: #64748b;
              font-size: 0.8rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-right: 0.5rem;
              flex-shrink: 0;
              min-width: 80px;
            }

            .table tbody td[data-label="ACTIONS"]::before,
            .table tbody td[data-label="Actions"]::before,
            .table tbody td[data-label="ACTION"]::before {
              display: none !important;
            }

            .table tbody td[data-label="ID"]{
              display: flex !important;
            }

            .table tbody td[data-label="PROGRAM"],
            .table tbody td[data-label="Program"],
            .table tbody td[data-label="PROGRAM NAME"] {
              align-items: flex-start !important;
              flex-direction: row !important;
            }

            .table tbody td[data-label="PROGRAM"] .program-text,
            .table tbody td[data-label="Program"] .program-text,
            .table tbody td[data-label="PROGRAM NAME"] .program-text {
              flex: 1;
              text-align: right;
              line-height: 1.3;
              word-wrap: break-word;
              white-space: normal;
              max-width: 60%;
              font-size: 0.85rem;
            }

            .table tbody td[data-label="PROGRAM"] > *:not(::before),
            .table tbody td[data-label="Program"] > *:not(::before),
            .table tbody td[data-label="PROGRAM NAME"] > *:not(::before) {
              flex: 1;
              text-align: right;
              line-height: 1.3;
              word-wrap: break-word;
              white-space: normal;
              max-width: 60%;
              font-size: 0.85rem;
            }

            .table tbody td[data-label="ACTIONS"],
            .table tbody td[data-label="Actions"],
            .table tbody td[data-label="ACTION"] {
              justify-content: center !important;
              padding: 0.75rem 1rem !important;
            }

            .table tbody td > * {
              margin: 0;
              flex-shrink: 0;
            }

            .table tbody td .btn {
              padding: 0.5rem 1rem;
              font-size: 0.85rem;
              border-radius: 8px;
              font-weight: 500;
            }

            .empty-state {
              text-align: center;
              padding: 3rem 1rem;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }

            .empty-state .icon {
              font-size: 3rem;
              margin-bottom: 1rem;
              opacity: 0.5;
            }
          }

          @media (min-width: 769px) {
            .table > tbody > tr {
              transition: all 0.2s ease;
            }
          }

          .table-loading {
            opacity: 0.7;
            pointer-events: none;
          }

          .fade-in {
            animation: fadeIn 0.3s ease-in;
          }

          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateY(10px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
        `}
      </style>


      {showMarkSheetModal && (
        <MarkSheetModal
          show={showMarkSheetModal}
          onHide={() => setShowMarkSheetModal(false)}
          student={selectedStudentForMarkSheet}
        />
      )}
    </>
  );
};

export default IFProgramEnrollStudent;
